import { useSeoApi } from '@/api/sdk-api';
import { AccountPromoModal } from '@/components/AccountPromoModal/AccountPromoModal';
import Header from '@/components/Header';
// import { IntegrationTokenAlert } from '@/components/IntegrationTokenAlert';
import { IntegrationTokenInfo } from '@/components/IntegrationTokenInfo';
import NotificationBackend from '@/components/NotificationBackend';
import { Tariffs } from '@/constants/tariff';
import {
  ACCOUNT_PRICE_PER_DAY,
  type AbTestsRaw,
  type SensitiveData,
  type TariffsResponse,
  type UserData,
} from '@/constants/Tariffs';
import { useLoadAbPackage } from '@/hooks/useLoadAbPackage';
import useLocalStorage from '@/hooks/useLocalStorage';
import { useSupportChatWidget } from '@/hooks/useSupportChatWidget';
import useURLParams from '@/hooks/useURLParams';
import { ChangeTokenModal } from '@/pages/finance/components/ChangeTokenModal';
import { usePaymentScript } from '@/pages/finance/hooks/usePaymentScript.ts';
import { useGetUserMeQuery } from '@/redux/api/user-me-api';
import { setGPTMessagesLimit } from '@/redux/slices/gpt-slice';
import { setTipsMode } from '@/redux/slices/tip-slice';
import {
  closeChangeTokenModal,
  selectChangeTokenModalOpen,
  selectChangeTokenModalProps,
} from '@/redux/slices/ui-slice';
import {
  type AutopaySettings,
  type GetAutopaySettingsResponse,
  type UserCard,
  type UserQuotas,
  selectActivePermission,
  selectHasPaidABTests,
  selectIsUnlimitedFinanceInfo,
  selectUserBalanceApproximateDayCount,
  selectUserBonusHasTime,
  selectUserBonusTime,
  selectUserCurrentTariffKey,
  setUser,
} from '@/redux/slices/user-slice';
import { type RootState } from '@/redux/store';
import { adaptAutopaySettings } from '@/utils/adapt-autopay-settings.function';
import {
  getAuthReturnCabinetAccountId,
  redirectToCurrentAuthSigninUrl,
} from '@/utils/create-auth-signin-url';
import { calculateProjectCost } from '@/utils/get-projects-cost';
import { dedupedTryRefresh, getSessionManager } from '@/utils/session';
import { PROJECT_NAME } from '@env';
import { type FinanceInfoOut } from '@marpla/api-sdk';
import { captureEvent, setSentryUser } from '@marpla/core';
import { FinanceActions } from '@marpla/core/enum';
import { useBalanceNotificationHook } from '@marpla/core/hooks/use-balance-notification.hook';
import { FreeTarifModal } from '@marpla/core/ui/components/FreeTarifModal/freeTarifModal';
import arrayEnd from '@marpla/helpers/arrayEnd';
import { BmNoticeVariants, TopBannerWithAction } from '@marpla/ui/components/TopBanner';
import { useMobileVersion } from '@marpla/ui/hooks/use-mobile-version.hook';
import Container from '@mui/material/Container';
import {
  type ReactNode,
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Helmet from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import styles from './index.module.scss';
import { IS_LOCALHOST, WS_AGENT, WS_GATEWAY_URL, emailToSimpleId } from '../constants/Constants';

interface Page {
  name: string;
  url: string;
  component: React.LazyExoticComponent<(props: { url: string }) => JSX.Element>;
  private: boolean;
}

const allPages: Page[] = [
  {
    name: 'Отчет по товару',
    url: '/report/project/:project/product/:article',
    component: lazy(
      () =>
        import('@/pages/report/App') as Promise<{
          default: (props: { url: string }) => JSX.Element;
        }>,
    ),
    private: true,
  },
  {
    name: 'SEO | MARPLA',
    url: '/report/project',
    component: lazy(
      () =>
        import('@/pages/projects/App') as Promise<{
          default: (props: { url: string }) => JSX.Element;
        }>,
    ),
    private: true,
  },
  {
    name: 'Список артикулов',
    url: '/report/project/:project',
    component: lazy(
      () =>
        import('@/pages/articles/App') as Promise<{
          default: (props: { url: string }) => JSX.Element;
        }>,
    ),
    private: true,
  },
  {
    name: 'Тарифы',
    url: '/finance',
    component: lazy(
      () =>
        import('@/pages/finance/App') as Promise<{
          default: (props: { url: string }) => JSX.Element;
        }>,
    ),
    private: true,
  },
  {
    name: 'Продажи2',
    url: '/sales2',
    component: lazy(
      () =>
        import('@/pages/sales2/App') as Promise<{
          default: (props: { url: string }) => JSX.Element;
        }>,
    ),
    private: true,
  },
  {
    name: 'Партнерка',
    url: '/partner',
    component: lazy(
      () =>
        import('@/pages/partner/App') as Promise<{
          default: (props: { url: string }) => JSX.Element;
        }>,
    ),
    private: true,
  },
  {
    name: 'Вход',
    url: '/signin',
    component: lazy(
      () =>
        import('@/pages/signin/App') as Promise<{
          default: (props: { url: string }) => JSX.Element;
        }>,
    ),
    private: false,
  },
];
const publicPages = allPages.filter((page) => !page.private);
const privatePages = allPages.filter((page) => page.private);

const initialPathname = window.location.pathname;

interface ShortFinanceInfo extends Pick<
  FinanceInfoOut,
  | 'balance'
  | 'balanceApproximateDayCount'
  | 'promo_days'
  | 'unlimited'
  | 'tariffs'
  | 'tariff_free_days'
> {
  tariffs: Record<
    string,
    {
      price: number;
      phrase_count_min: number;
      phrase_count_max: number;
    }
  >;
}

// const NotificationTariffModalBody = (
//   <>
//     {' '}
//     <Typography marginBottom="10px">Тариф «В помощь» будет удален в ближайшее время.</Typography>
//     <Typography marginBottom="10px">
//       Чтобы вы могли продолжить пользоваться ключевыми инструментами Marpla на выгодных условиях,
//       для пользователей тарифа «В помощь» действует
//       <Typography component="span" sx={{ fontWeight: '800 !important', color: '#8B0000' }}>
//         {' '}
//         специальное предложение до 08.04:
//       </Typography>
//     </Typography>
//     <Box marginBottom="10px">
//       <Typography component="ul" sx={{ listStyle: 'none', pl: 0, m: 0 }}>
//         <li>- персональная скидка на тарифы</li>
//         <li>- бесплатная консультация с экспертом сервиса</li>
//         <li>- рекомендации по продвижению и точки роста</li>
//       </Typography>
//     </Box>
//     <Typography sx={{ fontWeight: '800 !important', color: '#8B0000' }} marginBottom="10px">
//       Тариф «В помощь» будет деактивирован в ближайшее время.
//     </Typography>
//     <Typography marginBottom="10px">После отключения станут недоступны:</Typography>
//     <Typography component="ul" sx={{ listStyle: 'none', m: 0, pl: 0 }}>
//       <li>- модуль реклама</li>
//       <li>- модуль аналитики продаж</li>
//       <li>- АБ-тесты, если не оплачен пакет или те, что входят в ваш тариф модуль SEO</li>
//     </Typography>
//   </>
// );

function App(): ReactNode {
  const {
    token,
    setToken,
    getUserData,
    resetToken,
    getAccessSensitiveData,
    getFinanceInfo,
    getPaymentCards,
    getAbTests,
    getAutopaySettings,
    // getAbPackage,
    getTariffs,
  } = useSeoApi();

  const { prior_token: priorToken } = useURLParams() as { prior_token: string };
  const [isTipsMode] = useLocalStorage<boolean>('isTipsMode', false, { serializable: true });
  const location = useLocation();
  const dispatch = useDispatch();
  const [isAuthorized, setIsAuthorized] = useState<boolean>(
    !getSessionManager().checkIsTokenExpired(),
  );
  const [currentPage, setCurrentPage] = useState<Page | null>(null);
  const [isFreeTarifModalOpen, setIsFreeTarifModalOpen] = useState(false);

  const bidInfo = useRef({ userAccountCount: 0, salesAccountCount: 0 });
  const [userData, setUserData] = useState<{ email: string; phone: string } | null>(null);

  // ---section start: remove after adding when mobx, replase to $finance.getBalanceApproximateDayCount
  const balanceApproximateDayCount = useSelector(selectUserBalanceApproximateDayCount);
  const bonusTime = useSelector(selectUserBonusTime);
  const bonusHasTime = useSelector(selectUserBonusHasTime);
  const tariff = useSelector(selectUserCurrentTariffKey);
  const isUnlimitedFinanceInfo = useSelector(selectIsUnlimitedFinanceInfo);
  const hasPaidABTests = useSelector(selectHasPaidABTests);

  const financeInfoPromiseRef = useRef<Promise<ShortFinanceInfo> | null>(null);
  const financeInfoCacheRef = useRef<ShortFinanceInfo | null>(null);

  const [isCoreDataLoaded, setIsCoreDataLoaded] = useState(false);

  const ensureFinanceInfo = useCallback(async () => {
    if (financeInfoCacheRef.current) {
      return financeInfoCacheRef.current;
    }

    financeInfoPromiseRef.current ||= (async () => {
      const data = (await getFinanceInfo()) as ShortFinanceInfo;
      financeInfoCacheRef.current = data;

      return data;
    })();

    return financeInfoPromiseRef.current;
  }, [getFinanceInfo]);

  const getBalanceApproximateDayCountAsync = useCallback(async (): Promise<number | null> => {
    if (isNumber(balanceApproximateDayCount)) {
      const hasPromoPeriod = bonusTime?.hasTime;

      return hasPromoPeriod ? null : balanceApproximateDayCount;
    }

    try {
      const finance = await ensureFinanceInfo();
      const _balanceApproximateDayCount = finance?.balanceApproximateDayCount ?? null;
      const _promoDays = finance?.promo_days ?? null;
      const hasPromoPeriod = !!(_promoDays?.days || _promoDays?.hours || _promoDays?.minutes);

      dispatch(
        setUser({
          balanceApproximateDayCount: _balanceApproximateDayCount,
          bonusTime: {
            ..._promoDays,
            totalMinutes: _promoDays.days * 24 * 60 + _promoDays.hours * 60 + _promoDays.minutes,
            hasTime: hasPromoPeriod,
          },
          unlimitedFinanceInfo: finance.unlimited,
        }),
      );

      return hasPromoPeriod ? null : _balanceApproximateDayCount;
    } catch (e) {
      console.error('Failed to load balanceApproximateDayCount', e);

      return null;
    }
  }, [balanceApproximateDayCount, bonusTime, ensureFinanceInfo, dispatch]);
  // ---section end

  // const { navigateToFinance, closeBalanceBanner, variant, estimateBalanceDays, message } =
  const { navigateToFinance, variant, estimateBalanceDays, message } = useBalanceNotificationHook({
    getBalanceApproximateDayCount: getBalanceApproximateDayCountAsync,
    baseUrl: '/finance',
    returnBaseUrl: '',
    action: FinanceActions.top_up_balance,
  });

  usePaymentScript();
  useMobileVersion();

  // useEffect(() => {
  //   WS_AGENT.pingStart();

  //   const destroy = WS_AGENT.listen(WS_GATEWAY_URL, () => ({
  //     clientId: WS_CLIENT_ID,
  //   }));

  //   return () => {
  //     WS_AGENT.pingStop();
  //     destroy();
  //   };
  // }, []);

  useEffect(() => {
    const validateSession = async (): Promise<void> => {
      try {
        await dedupedTryRefresh();
        setIsAuthorized(!getSessionManager().checkIsTokenExpired());
      } catch {
        setIsAuthorized(false);
      }
    };

    if (isAuthorized === null) {
      void validateSession();
    }
  }, [isAuthorized]);

  const { data: me, isSuccess } = useGetUserMeQuery(undefined, {
    skip: !isAuthorized,
  });

  const accountId = getSessionManager().accountId;
  const selectedAccountId = useSelector((state: RootState) => state.user.accountId);
  const authReturnCabinetAccountId = getAuthReturnCabinetAccountId(selectedAccountId);
  const activePermission = useSelector(selectActivePermission);

  useSupportChatWidget({
    accountId: accountId ?? undefined,
    email: userData?.email,
    name: activePermission?.account.name,
    phone: userData?.phone,
  });

  const isOpen = useSelector(selectChangeTokenModalOpen);
  const modalProps = useSelector(selectChangeTokenModalProps);

  useEffect(() => {
    const validateSession = async (): Promise<void> => {
      try {
        await dedupedTryRefresh();

        if (getSessionManager().checkIsTokenExpired()) {
          throw new Error('Token expired');
        }

        setIsAuthorized(true);
      } catch {
        if (!IS_LOCALHOST) {
          redirectToCurrentAuthSigninUrl(authReturnCabinetAccountId);
        }

        setIsAuthorized(false);
      }
    };

    void validateSession();
  }, [authReturnCabinetAccountId]);

  useEffect(() => {
    if (priorToken) {
      setToken({ token: priorToken, isSession: false });
      window.location.href = initialPathname;
    }
  }, [setToken, priorToken]);

  useEffect(
    () => setCurrentPage(allPages.find((item) => item.url === location.pathname) || null),
    [location.pathname],
  );

  const title = currentPage ? currentPage.name : PROJECT_NAME;

  useEffect(() => {
    if (!isAuthorized) {
      return;
    }

    dispatch(setTipsMode(isTipsMode));
  }, [isAuthorized, isTipsMode, dispatch]);

  const { loadAbPackage } = useLoadAbPackage();

  useEffect(() => {
    if (!isAuthorized) return;

    let cancelled = false;

    const init = async (): Promise<void> => {
      try {
        const [user, tariffsResp, sensitive, finance, abTestsRaw, cards, autopayResp] =
          (await Promise.all([
            getUserData(),
            getTariffs(),
            getAccessSensitiveData(),
            ensureFinanceInfo(),
            getAbTests(),
            getPaymentCards(),
            getAutopaySettings(),
          ])) as [
            UserData,
            TariffsResponse,
            SensitiveData,
            ShortFinanceInfo | null,
            AbTestsRaw | null,
            UserCard[],
            GetAutopaySettingsResponse | null,
          ];

        const {
          email,
          phone,
          reg_date,
          code,
          question_limit,
          sales_account_count,
          account_count,
          account_weekly_product_order_sum,
          account_weekly_product_sale_sum,
          active_module_ab,
          expiration_date_module_ab,
        } = user;

        setUserData({ email, phone });

        bidInfo.current = {
          userAccountCount: account_count,
          salesAccountCount: sales_account_count,
        };

        if (!email && !phone) {
          captureEvent({ message: 'Error when receiving user data', level: 'error' });
        }

        if (isNumber(code)) {
          void resetToken();

          if (!IS_LOCALHOST) {
            redirectToCurrentAuthSigninUrl(authReturnCabinetAccountId);
          }

          return;
        }

        setSentryUser({ email, phone });

        // --- TARIFFS ---
        const resp = tariffsResp;

        const rawKey = Object.keys(resp).find((key) => resp[key].isCurrent);

        const normalizedTariffKey = rawKey === Tariffs.PAID ? Tariffs.PAID : Tariffs.DEFAULT;

        dispatch(
          setUser({
            email,
            phone,
            registrationDate: reg_date,
            isLoadingUserBasicData: false,
            stores: account_count,
            salesStores: sales_account_count,
            weeklyOrderSum: account_weekly_product_order_sum,
            weeklySaleSum: account_weekly_product_sale_sum,
            isActiveAB: active_module_ab,
            expirationABDate: expiration_date_module_ab,
            currentTariffKey: normalizedTariffKey,
          }),
        );

        dispatch(setGPTMessagesLimit(question_limit));

        // --- ACCESS ---
        const { unique_queries, projects, queries, sales_month } = sensitive as any;

        dispatch(
          setUser({
            accountId: getSessionManager().accountId,
            uniqueQueries: unique_queries,
            projects,
            phrases: queries,
            salesMonth: sales_month,
            isLoadingAccessSensitiveData: false,
          }),
        );

        // --- FINANCE ---
        if (!finance) {
          console.warn('finance not loaded');
        }

        // --- AB TESTS ---
        const abTests: UserQuotas = { quotas: [], unlimited: false };

        if (abTestsRaw?.quota) {
          abTests.quotas = [
            {
              id: abTestsRaw.quota.currentQuotaId,
              count: abTestsRaw.quota.quotaCount,
              expiredAt: new Date(abTestsRaw.quota.expiredAt),
              userId: abTestsRaw.quota.userId,
              price: abTestsRaw.quota.price,
              type: abTestsRaw.quota.type ?? null,
            },
          ];
        }

        abTests.unlimited = abTests.quotas.some(
          (quota) => quota.type === 'paid' && quota.count > 10,
        );

        // --- AUTOPAY ---
        let autopaySettings: AutopaySettings | null = null;

        if (autopayResp?.success && autopayResp.data) {
          autopaySettings = adaptAutopaySettings(autopayResp.data);
        }

        const hasPromoPeriod = !!(
          finance?.promo_days?.days ||
          finance?.promo_days?.hours ||
          finance?.promo_days?.minutes
        );

        const financeInfo = {
          ...finance,
          tariffs: Object.entries(finance?.tariffs ?? {}).map(([key, value], index) => ({
            ...value,
            phrase_count_min: value.phrase_count_min === 1 ? 0 : value.phrase_count_min,
            value: index + 1,
            title: key,
            price_per_day: Math.floor(value.price / 30),
          })),
          promo_days: {
            ...finance?.promo_days,
            totalMinutes:
              (finance?.promo_days?.days ?? 0) * 24 * 60 +
              (finance?.promo_days?.hours ?? 0) * 60 +
              (finance?.promo_days?.minutes ?? 0),
            hasTime: hasPromoPeriod,
          },
        };

        let currentTariff = financeInfo.tariffs.find(
          (item) =>
            unique_queries >= item.phrase_count_min && unique_queries <= item.phrase_count_max,
        );

        currentTariff ||= arrayEnd(financeInfo.tariffs)!;

        const accountCostPerDay =
          Math.max((bidInfo.current.userAccountCount || 0) - 1, 0) * ACCOUNT_PRICE_PER_DAY;
        const salesAccountCostPerDay =
          Math.max((bidInfo.current.salesAccountCount || 0) - 1, 0) * ACCOUNT_PRICE_PER_DAY;
        const projectCostPerDay = calculateProjectCost(projects);

        const isFreeTariff = normalizedTariffKey === Tariffs.DEFAULT;

        const dayPrice = isFreeTariff
          ? 0
          : Math.floor(currentTariff.price / 30) +
            accountCostPerDay +
            salesAccountCostPerDay +
            projectCostPerDay;

        // --- FINAL DISPATCH ---
        dispatch(
          setUser({
            abTests,
            balance: financeInfo.balance,
            balanceApproximateDayCount: finance?.balanceApproximateDayCount ?? null,
            tariffs: financeInfo.tariffs,
            dayPrice,
            tariffFreeDays: financeInfo.tariff_free_days,
            bonusTime: financeInfo.promo_days,
            isLoadingFinanceData: false,
            cards,
            autopaySettings,
          }),
        );

        await loadAbPackage();

        setIsCoreDataLoaded(true);

        if (!cancelled) {
          WS_AGENT.pingStart();
          WS_AGENT.listen(WS_GATEWAY_URL, () => ({
            clientId: emailToSimpleId(email),
          }));
        }
      } catch (err) {
        console.error(err);
      }
    };

    void init();

    return () => {
      cancelled = true;
      WS_AGENT.pingStop();
    };
    // TODO Возможно нужно только так:
    // [
    // 	isAuthorized,
    // 	token,
    // ]
  }, [
    getUserData,
    getAccessSensitiveData,
    getFinanceInfo,
    isAuthorized,
    resetToken,
    dispatch,
    token,
  ]);

  useEffect(() => {
    if (isSuccess && me) {
      dispatch(
        setUser({ permissions: me.permissions, activatedModuleTypes: me.activatedModuleTypes }),
      );
    }
  }, [isSuccess, me, dispatch]);

  useEffect(() => {
    if (!isCoreDataLoaded) return;

    const isFinancePage = location.pathname.startsWith('/finance');

    let shouldOpen = tariff === Tariffs.DEFAULT && !bonusHasTime && !isUnlimitedFinanceInfo;

    if (isFinancePage) {
      shouldOpen &&= !hasPaidABTests;
    }

    setIsFreeTarifModalOpen(shouldOpen);
  }, [
    isCoreDataLoaded,
    tariff,
    bonusHasTime,
    isUnlimitedFinanceInfo,
    hasPaidABTests,
    location.pathname,
  ]);

  const isWarningVarian = useMemo(() => variant === 'warning', [variant]);

  return (
    <Routes>
      <Route
        path="/*"
        element={
          // isAuthorized ?
          <>
            <Helmet>
              <title>{title}</title>
            </Helmet>
            <Container className={styles.container} maxWidth={false}>
              <Header />
              {/* <IntegrationTokenAlert
                permission={currentPermission ?? null}
                onAddToken={() => {
                  if (accountId) {
                    handleOpenApiKeyModal();
                  }
                }}
              /> */}
              {/* Инфоблок с предупреждениями об отсутствии API токенов (базового и сервисного) */}
              <IntegrationTokenInfo />
              <ChangeTokenModal
                open={isOpen}
                mode={modalProps?.mode ?? 'edit'}
                onClose={() => dispatch(closeChangeTokenModal())}
                onSubmitEnd={() => dispatch(closeChangeTokenModal())}
              />
              <div
                style={{
                  minHeight: 'calc(100dvh - 72px)',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <NotificationBackend />
                {variant && estimateBalanceDays !== null && !isUnlimitedFinanceInfo && (
                  <TopBannerWithAction
                    message={message}
                    actionLabel={'Пополнить баланс'}
                    variant={isWarningVarian ? BmNoticeVariants.WARNING : undefined}
                    onAction={navigateToFinance}
                    // onClose={closeBalanceBanner}
                  />
                )}

                <Routes>
                  {privatePages.map(({ url, component: Component }) => (
                    <Route
                      key={url}
                      path={url}
                      element={
                        <Suspense>
                          <Component url={url} />
                        </Suspense>
                      }
                    />
                  ))}

                  <Route path="*" element={<Navigate to="/report/project" />} />
                </Routes>
              </div>

              <FreeTarifModal
                open={isFreeTarifModalOpen}
                onClose={() => setIsFreeTarifModalOpen(false)}
              />
              <AccountPromoModal newAccountPromoActivated={Boolean(me?.newAccountPromoActivated)} />
            </Container>
          </>
          // ) : (
          // location.pathname !== '/forgot-password' && <Navigate to="/signin" />
          // )
        }
      />
      {publicPages.map(({ url, component: Component }) => (
        <Route
          key={url}
          path={url}
          element={
            <Container className={styles.container} maxWidth={false}>
              <Suspense>
                <Component url={url} />
              </Suspense>
            </Container>
          }
        />
      ))}
    </Routes>
  );
}

export default App;
